import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { DeliveryMethod, VisitDtoModelType } from '../../../../store/mst/models';
import AddressRow from './AddressRow';
import PhoneNumberRow from './PhoneNumberRow';
import { DetailsCard } from '../../../shared/DetailsCard';
import { filterRows } from './utils';
import CustomerProfileBadge from '../../../customerProfiles/CustomerProfileBadge';
import { getFilledFields } from '../../../customerProfiles/customerFilledFields';
import { withFromCustomer } from '../../../customerProfiles/FromCustomerMark';
import { isLandline } from '../../../../helpers/forms/phoneUtils';

interface CustomerDetailsSectionOptions {
  canEditVisits: boolean;
  deliveryMethod?: DeliveryMethod;
}

interface Props {
  visit: VisitDtoModelType;
  orderDetailsOptions: CustomerDetailsSectionOptions;
  onOpenCustomerProfile?: (source: HTMLElement) => void;
}

const CustomerDetailsSection: React.FC<Props> = ({ visit, orderDetailsOptions, onOpenCustomerProfile }) => {
  const { t } = useTranslation('common');
  const { canEditVisits, deliveryMethod } = orderDetailsOptions;
  const filled = getFilledFields(visit);
  const landlineWarning = deliveryMethod === DeliveryMethod.SMS && !!visit.phone && isLandline(visit.phone);

  const allRows: { label: string; value: React.ReactNode }[] = [
    {
      label: t('orderDetails.name'),
      value: visit.customerId
        ? <CustomerProfileBadge name={visit.name} onOpen={onOpenCustomerProfile} />
        : visit.name?.trim() || undefined,
    },
    {
      label: t('orderDetails.address'),
      value: visit.displayLocation
        ? withFromCustomer(
          <AddressRow visit={visit} canEditVisits={canEditVisits} />,
          filled.includes('address') && !visit.isInvalid && !visit.isQuestionable,
        )
        : undefined,
    },
    {
      label: t('orderDetails.phone'),
      value: visit.phone?.trim()
        ? withFromCustomer(
          <PhoneNumberRow phone={visit.phone} deliveryMethod={deliveryMethod} />,
          filled.includes('phone') && !landlineWarning,
        )
        : undefined,
    },
    {
      label: t('orderDetails.email'),
      value: visit.email?.trim() ? withFromCustomer(visit.email, filled.includes('email')) : undefined,
    },
  ];
  const rows = filterRows(allRows);

  if (rows.length === 0) {
    return null;
  }

  return (
    <DetailsCard
      data-testid="order-details-card"
      header={t('orderDetails.customer')}
      rows={rows}
    />
  );
};

export default observer(CustomerDetailsSection);
